import {
  LOCKED_DECISIONS, OBJECTIVES, SYSTEM_BEHAVIOURS, HALT_CRITERIA,
  CAN_ESTABLISH, CANNOT_ESTABLISH, STRATA_TARGETS,
} from '@/lib/betaConsole';
import type { PeriReviewRubric } from '@/lib/periReview';

function Card({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <section className="mb-13 rounded border border-border bg-surface p-13">
      {title ? <h2 className="mb-2 text-body font-semibold text-text-primary">{title}</h2> : null}
      {children}
    </section>
  );
}

function TermTable({ rows }: { rows: Array<{ term: string; body: string }> }) {
  return (
    <div className="tbl-wrap">
      <table className="tbl">
        <tbody>
          {rows.map((r) => (
            <tr key={r.term}>
              <td className="w-1/4 align-top font-semibold text-text-primary">{r.term}</td>
              <td className="align-top text-text-secondary">{r.body}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The pre-registered protocol. Static by design — this is the document the beta
 *  is run against, not console state. The primary metric and halt criteria are
 *  load-bearing: they are quoted, not paraphrased. */
export function ProtocolTab({ metric, rubric }: {
  metric: { scored: number; passing: number; pct: number | null };
  rubric?: PeriReviewRubric;
}) {
  return (
    <>
      <Card>
        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-text-muted">Primary objective</div>
        <p className="text-body text-text-primary">
          Does the board’s reasoning produce clinically sound, safe and useful output when it meets
          real users and real data?
        </p>
        <p className="mt-2 text-body-sm text-text-secondary">
          Synthetic validation tested internal consistency and plumbing. This is the first time the
          reasoning meets messy data and independent clinical scrutiny. Everything below is
          subordinate to that question — if a proposed addition to this beta does not serve it, it
          does not belong here.
        </p>
      </Card>

      <Card title="The shape of the thing">
        <p className="mb-2 text-body-sm text-text-secondary">
          Safety, accuracy and usefulness do not arrive together, and treating them as one
          longitudinal question is what makes the window feel fraught.
        </p>
        <p className="mb-2 text-body-sm text-text-secondary">
          <strong className="text-text-primary">Accuracy and safety are baseline questions.</strong>{' '}
          The moment a participant’s baseline report sits next to their bloods, history, symptom
          picture and stage, a reviewer can say whether the reasoning is sound and whether anything
          unsafe got through. That verdict lands within a fortnight of onboarding — not at month
          three.
        </p>
        <p className="text-body-sm text-text-secondary">
          <strong className="text-text-primary">Only usefulness and engagement need the longitudinal window.</strong>{' '}
          Do they keep using it, does it help, does re-running on new data evolve sensibly rather
          than thrash.
        </p>
      </Card>

      <Card title="Locked decisions"><TermTable rows={LOCKED_DECISIONS} /></Card>

      <Card title="What we are learning">
        <p className="mb-2 text-body-sm text-text-muted">Priority order. Where two conflict, the higher one wins.</p>
        <div className="tbl-wrap">
          <table className="tbl">
            <tbody>
              {OBJECTIVES.map((o) => (
                <tr key={o.n}>
                  <td className="w-8 align-top font-mono text-[12px] text-text-muted">{o.n}</td>
                  <td className="w-1/5 align-top">
                    <span className="font-semibold text-text-primary">{o.name}</span>
                    {o.halt ? (
                      <span className="mt-1 block w-fit rounded-pill px-2 py-0.5 text-[11px] font-semibold mod-json">
                        halt authority
                      </span>
                    ) : null}
                  </td>
                  <td className="align-top text-text-secondary">{o.body}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="System behaviours to watch">
        <p className="mb-2 text-body-sm text-text-muted">
          Known architecture that becomes user-visible the moment real people are in the system.
          Each has a home in the findings log.
        </p>
        <TermTable rows={SYSTEM_BEHAVIOURS} />
      </Card>

      <Card title="Primary metric, pre-registered">
        {/* Read from the LIVE rubric, never restated here. The July draft of this
            page paraphrased the metric as "no safety issue and substantive
            concordance" — but the rubric scores `no_issue` / `concordant` across
            seven categories, so the paraphrase named values that do not exist.
            A page whose whole claim is that the metric was fixed in advance must
            not carry its own copy of it. */}
        {rubric ? (
          <div className="mb-2 rounded border-l-2 border-accent bg-surface-sunken p-13">
            <p className="text-body-sm text-text-primary">
              <strong>{rubric.primary_metric.label}</strong>
              {rubric.primary_metric.description ? <> — {rubric.primary_metric.description}</> : null}
            </p>
            <p className="mt-2 text-body-sm text-text-secondary">
              Passes when{' '}
              {Object.entries(rubric.primary_metric.pass_rule).map(([cat, allowed], i, arr) => (
                <span key={cat}>
                  <code className="font-mono text-[12px]">{cat}</code> is{' '}
                  {allowed.map((v) => <code key={v} className="font-mono text-[12px]">{v}</code>)
                    .reduce((a, b) => <>{a} or {b}</>)}
                  {i < arr.length - 1 ? ' and ' : ''}
                </span>
              ))}
              . Rubric v{rubric.version}
              {rubric.status ? <> · <strong>{rubric.status}</strong></> : null}.
            </p>
          </div>
        ) : (
          <p className="mb-2 text-body-sm text-flagged">
            Rubric unavailable — the metric is defined by <code>/advisor/peri-review-rubric</code> and
            is deliberately not restated on this page.
          </p>
        )}
        <p className="text-body-sm text-text-secondary">
          Current:{' '}
          <strong className="font-mono text-text-primary">
            {metric.scored ? `${metric.passing}/${metric.scored}` : 'no baseline reviews complete'}
          </strong>
          {metric.pct !== null ? <> · {metric.pct}%</> : null}
        </p>
        <p className="mt-2 text-body-sm text-text-muted">
          The threshold is committed before any data exists, because a number chosen after the fact
          is not a threshold — it is a rationalisation. The secondary output is the disagreement
          catalogue: every reviewer disagreement, categorised, feeding the backlog directly.
        </p>
      </Card>

      <Card title="Safety halt criteria">
        <div className="rounded border-l-2 border-flagged bg-surface-sunken p-13">
          <ul className="ml-4 list-disc text-body-sm text-text-primary">
            {HALT_CRITERIA.map((c) => <li key={c} className="mb-1 last:mb-0">{c}</li>)}
          </ul>
        </div>
        <p className="mt-2 text-body-sm text-text-muted">
          These are pre-registered so that the decision to halt is made now, in the cold, rather
          than in the moment when halting is expensive and you are invested.
        </p>
      </Card>

      <Card title="Scope caveats">
        <p className="mb-2 text-body-sm text-text-muted">
          Kept visible, in the protocol, at the top level. An investor or advisor who finds these
          unstated later will discount everything above them.
        </p>
        <h3 className="mb-1 mt-3 text-body-sm font-semibold text-text-primary">This beta can establish</h3>
        <ul className="ml-4 list-disc text-body-sm text-text-secondary">
          {CAN_ESTABLISH.map((c) => <li key={c} className="mb-1">{c}</li>)}
        </ul>
        <h3 className="mb-1 mt-3 text-body-sm font-semibold text-text-primary">This beta cannot establish</h3>
        <TermTable rows={CANNOT_ESTABLISH} />
      </Card>

      <Card title="Cohort composition">
        <p className="mb-2 text-body-sm text-text-secondary">
          Twelve, purposively sampled. The strata exist to cover the design space, not to balance a
          population. Stage and sub_branch targets are tracked live on the{' '}
          <strong className="text-text-primary">Cohort</strong> tab against the peri-review cohort store.
        </p>
        <TermTable rows={STRATA_TARGETS.map((s) => ({ term: `${s.label} — ${s.target}`, body: s.rationale }))} />
        <div className="mt-2 rounded border-l-2 border-flagged bg-surface-sunken p-13">
          <p className="text-body-sm text-text-primary">
            <strong>Exclusion, held firm:</strong> oestrogen-sensitive history is excluded until the
            botanical safety gate ships and re-runs clean. Medical Advisory owns the full exclusion
            list — this one is not negotiable in the meantime, and the screening question must be
            verified to actually filter.
          </p>
        </div>
      </Card>
    </>
  );
}
